import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
import { PhxDatePickerBase } from './phx-date-picker-base';
import { PhxDatePickerComponent, PhxDatePickerDropdownOffset } from './phx-date-picker.component';
import { PhxTooltipDirective } from '../tooltip/phx-tooltip.directive';
import { Calendar } from '../icon/icon-map';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { isValid, isAfter, isBefore, startOfDay, isSameDay, format } from 'date-fns';
import { PHX_DATE_FORMAT, SelectorSize, PhxDateInputField, PhxNonNullDateInputField, PhxDateFormatConfig, DATE_INPUT_FIELD, PhxDateViolationType } from './utils/types';
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_FORMAT_WITHOUT_YEAR, HIDE_YEAR_FORMAT_MAPPER, MONTH_NAMES_BY_LOCALE, PHX_DATE_INPUT_FORMAT_CONFIGS, DIACRITICS_REGEX, SINGLE_DIGIT_REGEX, SINGLE_UNICODE_LETTER_REGEX, ALPHA_CHAR_REGEX, DIGIT_CHAR_REGEX } from './utils/constants';
import { PhxTranslationService } from './../../translation/translation.service';
import { PhxTranslatePipe } from '../../translation/phx-translation.pipe';
import { PHX_SIZE } from './../../constants';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider, PhxDateFormatStyle } from '../locale/phx-locale.token';
import { PhxDatePickerService } from './date-picker.service';

@Component({
  selector: 'phx-date-input',
  templateUrl: './phx-date-picker-input.component.html',
  styleUrls: ['./phx-date-picker-input.component.scss'],
  inputs: ['phxDisabled'],
  host: {
    class: 'phx-date-picker-input',
    '[class.phx-date-picker-input-error]': 'phxHasError || hasInternalError',
    '[class.phx-date-picker-input-small]': `phxSize === '${PHX_SIZE.SM}'`,
    '[class.phx-date-picker-input-medium]': `phxSize === '${PHX_SIZE.MD}'`,
    '[class.phx-date-picker-input-large]': `phxSize === '${PHX_SIZE.LG}'`,
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxDatePickerInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhxDatePickerInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxDatePickerInputComponent extends PhxDatePickerBase implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy {
  @ViewChild('dateInputBox') private _dateInputBox: ElementRef<HTMLElement>;
  @ViewChild('inputTooltip', { static: false }) private _inputTooltip: PhxTooltipDirective;
  @ViewChild(PhxDatePickerComponent) private _datePicker: PhxDatePickerComponent;

  @Input() public phxDateFormat: PHX_DATE_FORMAT = PHX_DATE_FORMAT['MMM dd, yyyy'];
  @Input() public phxSize: SelectorSize = PHX_SIZE.MD;
  @Input() public phxHideYear: boolean = false;

  public readonly CalendarIcon: string = Calendar;
  public month: number | null = null;
  public day: number | null = null;
  public year: number | null = null;
  public focusedField: PhxDateInputField = null;
  public dropdownOpen: boolean = false;
  public internalDateValue: Date | null = null;
  public tooltipTitle: string | null = null;
  public tooltipContent: string | null = null;
  public hasInternalError: boolean = false;
  public readonly PHX_SIZE = PHX_SIZE;

  private _isFocused: boolean = false;
  private _skipFocusFieldOnNextFocus: boolean = false;
  private _inputBuffer: string = '';
  private _inputBufferTimeout: any = null;
  private _tooltipAutoHideTimeout: any = null;

  private _onChange = (_: any) => { };
  private _onTouched = () => { };

  private _cachedLocaleMonthNames: string[] | null = null;

  public formatConfig: PhxDateFormatConfig = PHX_DATE_INPUT_FORMAT_CONFIGS[PHX_DATE_FORMAT['MMM dd, yyyy']];
  public displayFieldOrder: PhxNonNullDateInputField[] = [];
  public displaySeparators: string[] = [];

  constructor(
    private _cdr: ChangeDetectorRef,
    private _translationService: PhxTranslationService,
    private _phxTranslate: PhxTranslatePipe,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private _phxLocale: PhxLocaleProvider | null,
    private _datePickerService: PhxDatePickerService,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.formatConfig = this.computeFormatConfig();
    this.recomputeDisplayConfig();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxDateFormat, phxHideYear } = changes;

    if (phxDateFormat || phxHideYear) {
      this.formatConfig = this.computeFormatConfig();
      this.recomputeDisplayConfig();
      this._cdr.markForCheck();
    }
  }

  public ngOnDestroy(): void {
    this.clearTooltipAutoHideTimeout();
    this.clearInputBuffer();
  }

  private computeFormatConfig(): PhxDateFormatConfig {
    if (this._phxLocale?.formatDateToParts) {
      const parts = this._phxLocale.formatDateToParts(PhxDatePickerInputComponent.REFERENCE_DATE);
      const parsed = this.parseIntlParts(parts);
      if (parsed && parsed.fieldOrder.length === 3 && parsed.separators.length >= 2) {
        return {
          fieldOrder: [parsed.fieldOrder[0], parsed.fieldOrder[1], parsed.fieldOrder[2]],
          separators: [parsed.separators[0], parsed.separators[1]],
          monthDisplay: parsed.monthDisplay,
        };
      }
    }

    if (this.phxDateFormat) {
      const legacyConfig = PHX_DATE_INPUT_FORMAT_CONFIGS[this.phxDateFormat];
      if (legacyConfig) return legacyConfig;
    }

    return PHX_DATE_INPUT_FORMAT_CONFIGS[PHX_DATE_FORMAT['MMM dd, yyyy']];
  }

  public get fieldOrder(): [PhxNonNullDateInputField, PhxNonNullDateInputField, PhxNonNullDateInputField] {
    return this.formatConfig.fieldOrder;
  }

  public get separators(): [string, string] {
    return this.formatConfig.separators;
  }

  public get isAlphaMonth(): boolean {
    return this.formatConfig.monthDisplay === 'alpha';
  }

  private recomputeDisplayConfig(): void {
    if (!this.phxHideYear) {
      this.displayFieldOrder = [...this.fieldOrder];
      this.displaySeparators = [...this.separators];
      return;
    }

    const parsed = this.parseWithoutYearFromIntl();
    if (parsed) {
      this.displayFieldOrder = parsed.fieldOrder;
      this.displaySeparators = parsed.separators;
      return;
    }

    this.displayFieldOrder = this.fieldOrder.filter(f => f !== DATE_INPUT_FIELD.YEAR) as PhxNonNullDateInputField[];
    const yearIndex = this.fieldOrder.indexOf(DATE_INPUT_FIELD.YEAR);
    this.displaySeparators = yearIndex === 0 ? [this.separators[1]] : [this.separators[0]];
  }

  private parseWithoutYearFromIntl(): { fieldOrder: PhxNonNullDateInputField[]; separators: string[] } | null {
    if (!this._phxLocale?.formatDateToParts) return null;
    const parts = this._phxLocale.formatDateToParts(PhxDatePickerInputComponent.REFERENCE_DATE);
    const parsed = this.parseIntlParts(parts);
    if (!parsed) return null;

    const yearIdx = parsed.fieldOrder.indexOf(DATE_INPUT_FIELD.YEAR);
    if (yearIdx === -1) return parsed;

    const fieldOrder = parsed.fieldOrder.filter(f => f !== DATE_INPUT_FIELD.YEAR);
    const separators = [...parsed.separators];

    // Remove the separator adjacent to the year field
    if (yearIdx === 0) {
      separators.shift();
    } else {
      separators.splice(yearIdx - 1, 1);
    }

    return { fieldOrder, separators };
  }

  public get combinedValue(): Date | null {
    if (this.month === null || this.day === null) {
      return null;
    }

    const yearValue = this.phxHideYear ? (this.year ?? new Date().getFullYear()) : this.year;
    if (yearValue === null) {
      return null;
    }

    const date = new Date(yearValue, this.month - 1, this.day);
    date.setHours(0, 0, 0, 0);

    // Validate that the date is valid (e.g., Feb 30 would be invalid)
    if (date.getMonth() !== this.month - 1 || date.getDate() !== this.day) {
      return null;
    }

    return date;
  }

  // Size based dropdown offset
  public get dropdownOffset(): PhxDatePickerDropdownOffset {
    const padding = this.dropdownOffsetBySize;
    return { x: -padding.left, y: padding.bottom };
  }

  private get dropdownOffsetBySize(): { left: number; bottom: number } {
    switch (this.phxSize) {
      case PHX_SIZE.SM:
        return { left: 2, bottom: 2 };
      case PHX_SIZE.LG:
        return { left: 10, bottom: 10 };
      default:
        return { left: 6, bottom: 6 };
    }
  }

  /**
   * Returns a date for calendar navigation purposes, using current date as fallback for missing fields.
   * This ensures the calendar shows the correct year/month even when input is incomplete.
   */
  private get calendarViewDate(): Date {
    const now = new Date();
    const year = this.year ?? now.getFullYear();
    const month = this.month !== null ? this.month - 1 : now.getMonth();
    const day = this.day ?? 1;

    return new Date(year, month, day, 0, 0, 0, 0);
  }

  private get localeMonthNames(): string[] {
    if (this._cachedLocaleMonthNames) return this._cachedLocaleMonthNames;

    if (this._phxLocale?.getAllMonthsOfYearArray) {
      this._cachedLocaleMonthNames = this._phxLocale.getAllMonthsOfYearArray();
      return this._cachedLocaleMonthNames;
    }

    const localize = this._datePickerService.getDateFnsLocale()?.localize;
    if (localize?.month) {
      this._cachedLocaleMonthNames = Array.from({ length: 12 }, (_, i) =>
        localize.month(i, { width: 'abbreviated' })
      );
      return this._cachedLocaleMonthNames;
    }
    const locale = this._translationService.getLocale();
    this._cachedLocaleMonthNames = MONTH_NAMES_BY_LOCALE[locale] || MONTH_NAMES_BY_LOCALE['en'];
    return this._cachedLocaleMonthNames;
  }

  public get formattedMonth(): string {
    if (this.month === null) return this.isAlphaMonth ? 'MMM' : 'MM';
    if (this.isAlphaMonth) return this.localeMonthNames[this.month - 1] || 'MMM';
    return this.month.toString().padStart(2, '0');
  }

  public get formattedDay(): string {
    if (this.day === null) return 'DD';
    return this.day.toString().padStart(2, '0');
  }

  public get formattedYear(): string {
    if (this.year === null) return 'YYYY';
    return this.year.toString().padStart(4, '0');
  }

  public getFormattedValue(field: PhxNonNullDateInputField): string {
    switch (field) {
      case DATE_INPUT_FIELD.MONTH: return this.formattedMonth;
      case DATE_INPUT_FIELD.DAY: return this.formattedDay;
      case DATE_INPUT_FIELD.YEAR: return this.formattedYear;
    }
  }

  public get isPlaceholder(): boolean {
    if (this.phxDisabled) return true;
    if (this.phxHideYear) return this.month === null || this.day === null;
    return this.month === null || this.day === null || this.year === null;
  }


  public setFocusedField(field: PhxDateInputField): void {
    if (this.phxDisabled) return;
    this.focusedField = field;
    this.clearInputBuffer();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.phxDisabled) return;

    if (event.key === KeyboardKeys.TAB) {
      return;
    }

    // Keyboard Enter/Space on calendar icon: open dropdown and focus calendar (no click)
    const isCalendarButton = (event.target as HTMLElement)?.closest('.phx-input-calendar-icon');
    if (isCalendarButton && (event.code === KeyboardKeys.SPACE || event.code === KeyboardKeys.ENTER)) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.dropdownOpen) {
        this.dropdownOpen = true;
        this.internalDateValue = this.combinedValue;
        this._datePicker?.updateCalendarView(this.calendarViewDate);
        this.focusCalendarBody();
        this._cdr.detectChanges();
      }
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.key === 'Backspace' && this.focusedField) {
      this.resetFocusedField();
      this.propagateChange();
      return;
    }

    if (event.key === KeyboardKeys.ARROW_UP || event.key === KeyboardKeys.ARROW_DOWN) {
      const increment = event.key === KeyboardKeys.ARROW_UP ? 1 : -1;
      this.handleArrowUpDown(increment);
      this.propagateChange();
      return;
    }

    if (event.key === KeyboardKeys.ARROW_RIGHT) {
      this.dismissTooltip();
      this.dropdownOpen = false;
      this.navigateToNextField();
      this.focusDateInputBox();
      this._cdr.detectChanges();
      return;
    }

    if (event.key === KeyboardKeys.ARROW_LEFT) {
      this.dismissTooltip();
      this.dropdownOpen = false;
      this.navigateToPreviousField();
      this.focusDateInputBox();
      this._cdr.detectChanges();
      return;
    }

    if (SINGLE_DIGIT_REGEX.test(event.key)) {
      this.handleDigitInput(event.key);
      this.propagateChange();
      return;
    }

    if (SINGLE_UNICODE_LETTER_REGEX.test(event.key) && this.isAlphaMonth && this.focusedField === DATE_INPUT_FIELD.MONTH) {
      this.handleMonthAlphaInput(event.key);
      this.propagateChange();
      return;
    }

    if (event.key.length === 1 && (!this.isAlphaMonth || !(this.focusedField === DATE_INPUT_FIELD.MONTH))) {
      this.showTooltipMessage(null, this._phxTranslate.transform('Please enter numeric values'), 3000);
    }
  }

  public onFocus(): void {
    if (this.phxDisabled) return;
    this._isFocused = true;

    if (this._skipFocusFieldOnNextFocus) {
      this._skipFocusFieldOnNextFocus = false;
      return;
    }

    if (!this.focusedField) this.focusedField = this.displayFieldOrder[0];
    if (this.month === null || this.day === null || (!this.phxHideYear && this.year === null)) return;

    const value = this.combinedValue;
    const violation = value ? this.getDateViolationType(value) : null;
    if (violation) {
      this.showTooltipMessage(null, this.getErrorMessageForViolation(violation), 5000);
      return;
    }

    this.showTooltipMessage(this._phxTranslate.transform('Date format'), this.resolvedFormatPattern);
  }

  public onBlur(): void {
    this._isFocused = false;
    this._onTouched();

    const value = this.combinedValue;
    const violation = value ? this.getDateViolationType(value) : null;
    if (violation) {
      this.hasInternalError = true;
    }

    this.focusedField = null;
    this.clearInputBuffer();
    this.dismissTooltip();
    this._cdr.detectChanges();
  }

  public toggleDropdown(): void {
    if (this.phxDisabled) return;
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.internalDateValue = this.combinedValue;
      this._datePicker?.updateCalendarView(this.calendarViewDate);
    }
    this._cdr.detectChanges();
  }

  public onDropdownVisibilityChange(open: boolean): void {
    this.dropdownOpen = open;
    if (open) {
      this.internalDateValue = this.combinedValue;
      this._datePicker?.updateCalendarView(this.calendarViewDate);
    } else {
      this.focusedField = null;
      this._skipFocusFieldOnNextFocus = true;
      setTimeout(() => this._dateInputBox?.nativeElement.focus());
    }
    this._cdr.detectChanges();
  }

  private focusCalendarBody(): void {
    // Delay to allow dropdown directive's auditTime(50) and overlay attach to complete
    setTimeout(() => {
      const body = document.querySelector('.phx-date-picker-body') as HTMLElement;
      body?.focus();
    }, 200);
  }

  private focusDateInputBox(): void {
    setTimeout(() => this._dateInputBox?.nativeElement.focus(), 0);
  }

  public onDatePickerChange(date: Date | null): void {
    if (!date) return;
    this.setInternalValues(date);
    this.propagateChange();
  }

  public onClear(): void {
    this.setInternalValues(null);
    this.propagateChange();
  }

  public registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.phxDisabled = isDisabled;
  }

  public writeValue(value: Date | null): void {
    this.setInternalValues(value);
  }

  private resetFocusedField(): void {
    if (!this.focusedField) return;
    switch (this.focusedField) {
      case DATE_INPUT_FIELD.MONTH:
        this.month = null;
        break;
      case DATE_INPUT_FIELD.DAY:
        this.day = null;
        break;
      case DATE_INPUT_FIELD.YEAR:
        this.year = null;
        break;
    }
    this.clearInputBuffer();
    this._cdr.markForCheck();
  }

  private setInternalValues(value: Date | null): void {
    if (!value) {
      this.month = null;
      this.day = null;
      this.year = null;
      this.internalDateValue = null;
      this.hasInternalError = false;
      return;
    }

    const date = new Date(value);
    this.month = date.getMonth() + 1;
    this.day = date.getDate();
    this.year = date.getFullYear();
    this.internalDateValue = date;
    this.hasInternalError = !!this.getDateViolationType(date);
    this._cdr.detectChanges();
  }

  private isDateInEnabledDates(date: Date): boolean {
    if (!this.phxEnabledDates?.length) return true;
    return this.phxEnabledDates.some(enabledDate => isSameDay(date, enabledDate));
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      this.hasInternalError = false;
      return null;
    }

    if (!isValid(value)) {
      if (!this._isFocused) this.hasInternalError = true;
      return { invalidDate: true };
    }

    const violation = this.getDateViolationType(value);
    if (!violation) {
      this.hasInternalError = false;
      return null;
    }

    if (!this._isFocused) this.hasInternalError = true;

    switch (violation) {
      case 'min': return { minDate: true };
      case 'max': return { maxDate: true };
      case 'range':
        const beforeMin = isBefore(value, startOfDay(this.phxMinSelectableDate));
        return beforeMin ? { minDate: true } : { maxDate: true };
      case 'disabled': return { disabledDate: true };
    }
  }

  private handleArrowUpDown(increment: number): void {
    const isIncreasing = increment > 0;
    switch (this.focusedField) {
      case DATE_INPUT_FIELD.MONTH:
        this.month = this.month === null
          ? (isIncreasing ? 1 : 12)
          : this.adjustValue(this.month, 1, 12, increment);
        this.clampDayToMonth();
        break;
      case DATE_INPUT_FIELD.DAY:
        const maxDay = this.getMaxDayForMonth();
        this.day = this.day === null
          ? (isIncreasing ? 1 : maxDay)
          : this.adjustValue(this.day, 1, maxDay, increment);
        break;
      case DATE_INPUT_FIELD.YEAR:
        if (this.year === null) {
          this.year = new Date().getFullYear();
        } else {
          const minYear = this.phxMinSelectableDate?.getFullYear();
          const maxYear = this.phxMaxSelectableDate?.getFullYear();
          const newYear = this.year + increment;

          if (minYear != null && newYear < minYear) {
            this.year = minYear;
          } else if (maxYear != null && newYear > maxYear) {
            this.year = maxYear;
          } else {
            this.year = newYear;
          }
        }
        break;
    }
  }

  private handleDigitInput(key: string): void {
    // Clear buffer if switching from alpha to digit input
    if (this._inputBuffer && ALPHA_CHAR_REGEX.test(this._inputBuffer)) {
      this.clearInputBuffer();
    }

    this._inputBuffer += key;

    // Clear any existing timeout
    if (this._inputBufferTimeout) {
      clearTimeout(this._inputBufferTimeout);
    }

    switch (this.focusedField) {
      case DATE_INPUT_FIELD.MONTH:
        this.handleMonthInput();
        break;
      case DATE_INPUT_FIELD.DAY:
        this.handleDayInput();
        break;
      case DATE_INPUT_FIELD.YEAR:
        this.handleYearInput();
        break;
    }

    // Set timeout to clear buffer after delay
    this._inputBufferTimeout = setTimeout(() => {
      this.clearInputBuffer();
    }, 5000);
  }

  private handleMonthInput(): void {
    const value = parseInt(this._inputBuffer, 10);

    if (this._inputBuffer.length < 2 && value <= 1) {
      this.month = value;
      return;
    }

    this.month = Math.min(Math.max(value, 1), 12);
    this.clampDayToMonth();
    this.clearInputBuffer();
    this.focusedField = this.getNextField(DATE_INPUT_FIELD.MONTH);
  }

  private handleMonthAlphaInput(key: string): void {
    if (this._inputBuffer && DIGIT_CHAR_REGEX.test(this._inputBuffer)) {
      this.clearInputBuffer();
    }

    this._inputBuffer += key.toLowerCase();

    if (this._inputBufferTimeout) {
      clearTimeout(this._inputBufferTimeout);
    }

    const buffer = this.stripDiacritics(this._inputBuffer);
    const monthNames = this.localeMonthNames;
    const matchingMonthIndices: Set<number> = new Set();

    for (let i = 0; i < 12; i++) {
      if (this.stripDiacritics(monthNames[i].toLowerCase()).startsWith(buffer)) {
        matchingMonthIndices.add(i);
      }
    }

    if (matchingMonthIndices.size === 0) {
      this.clearInputBuffer();
      return;
    }

    const firstMatch = Array.from(matchingMonthIndices)[0];
    this.month = firstMatch + 1;
    this.clampDayToMonth();

    if (matchingMonthIndices.size === 1 || buffer.length >= 3) {
      this.clearInputBuffer();
      this.focusedField = this.getNextField(DATE_INPUT_FIELD.MONTH);
      return;
    }

    this._inputBufferTimeout = setTimeout(() => {
      this.clearInputBuffer();
      this.focusedField = this.getNextField(DATE_INPUT_FIELD.MONTH);
      this._cdr.detectChanges();
    }, 1000);
  }

  private handleDayInput(): void {
    const value = parseInt(this._inputBuffer, 10);
    const maxDay = this.getMaxDayForMonth();

    if (this._inputBuffer.length < 2 && value <= 3) {
      this.day = value;
      return;
    }

    this.day = Math.min(Math.max(value, 1), maxDay);
    this.clearInputBuffer();
    this.focusedField = this.getNextField(DATE_INPUT_FIELD.DAY);
  }

  private handleYearInput(): void {
    this._inputBuffer = this._inputBuffer.replace(/^0+/, '');

    if (!this._inputBuffer) {
      return;
    }

    const value = parseInt(this._inputBuffer, 10);

    if (this._inputBuffer.length < 4) {
      this.year = value;
      return;
    }

    this.year = value;
    this.clearInputBuffer();
    this.focusedField = this.getNextField(DATE_INPUT_FIELD.YEAR);
  }

  private getNextField(current: PhxNonNullDateInputField): PhxNonNullDateInputField {
    const order = this.displayFieldOrder;
    const idx = order.indexOf(current);
    return order[(idx + 1) % order.length];
  }

  private getPreviousField(current: PhxNonNullDateInputField): PhxNonNullDateInputField {
    const order = this.displayFieldOrder;
    const idx = order.indexOf(current);
    return order[(idx - 1 + order.length) % order.length];
  }

  private navigateToNextField(): void {
    this.focusedField = this.focusedField
      ? this.getNextField(this.focusedField)
      : this.displayFieldOrder[0];
    this.clearInputBuffer();
  }

  private navigateToPreviousField(): void {
    this.focusedField = this.focusedField
      ? this.getPreviousField(this.focusedField)
      : this.displayFieldOrder[this.displayFieldOrder.length - 1];
    this.clearInputBuffer();
  }

  private getMaxDayForMonth(): number {
    if (this.month === null || this.year === null) {
      return 31;
    }
    return new Date(this.year, this.month, 0).getDate();
  }

  private adjustValue(current: number, min: number, max: number, increment: number): number {
    const newValue = current + increment;
    if (newValue > max) return min;
    if (newValue < min) return max;
    return newValue;
  }

  private propagateChange(): void {
    const value = this.combinedValue;
    this._onChange(value);

    if (!value) {
      this.dismissTooltip();
      return;
    }

    this.internalDateValue = value;
    const violation = this.getDateViolationType(value);
    if (violation) {
      this.showTooltipMessage(null, this.getErrorMessageForViolation(violation), 5000);
      return;
    }

    this.hasInternalError = false;
    this.dismissTooltip();
  }

  private clampDayToMonth(): void {
    if (this.day === null) return;

    const maxDay = this.getMaxDayForMonth();
    if (this.day > maxDay) {
      this.day = maxDay;
    }
  }


  private getDateViolationType(date: Date): PhxDateViolationType | null {
    const hasMin = !!this.phxMinSelectableDate;
    const hasMax = !!this.phxMaxSelectableDate;
    const beforeMin = hasMin && isBefore(date, startOfDay(this.phxMinSelectableDate));
    const afterMax = hasMax && isAfter(date, startOfDay(this.phxMaxSelectableDate));

    if (hasMin && hasMax && (beforeMin || afterMax)) return 'range';
    if (beforeMin) return 'min';
    if (afterMax) return 'max';
    if (this.phxEnabledDates?.length && !this.isDateInEnabledDates(date)) return 'disabled';
    return null;
  }

  private getErrorMessageForViolation(type: PhxDateViolationType): string {
    switch (type) {
      case 'min':
        return `${this._phxTranslate.transform('Please select a date after')} ${this.formatDateForMessage(this.phxMinSelectableDate)}`;
      case 'max':
        return `${this._phxTranslate.transform('Please select a date before')} ${this.formatDateForMessage(this.phxMaxSelectableDate)}`;
      case 'range':
        return `${this._phxTranslate.transform('Please select a date between')} ${this.formatDateForMessage(this.phxMinSelectableDate)} ${this._phxTranslate.transform('and')} ${this.formatDateForMessage(this.phxMaxSelectableDate)}`;
      case 'disabled':
        return this._phxTranslate.transform('Please select a valid date');
    }
  }

  private get resolvedFormatPattern(): string {
    if (this._phxLocale?.formatDateToParts) {
      const parts = this._phxLocale.formatDateToParts(PhxDatePickerInputComponent.REFERENCE_DATE);
      const tokens = parts
        .filter(p => this.phxHideYear ? p.type !== 'year' : true)
        .map(p => {
          if (p.type === 'day') return 'DD';
          if (p.type === 'year') return 'YYYY';
          if (p.type === 'month') return /^\d+$/.test(p.value) ? 'MM' : 'MMM';
          return p.value;
        });

      const FIELD_TOKENS = new Set(['DD', 'MM', 'MMM', 'YYYY']);
      // Trim leading/trailing literals left after year removal
      while (tokens.length && !FIELD_TOKENS.has(tokens[0])) tokens.shift();
      while (tokens.length && !FIELD_TOKENS.has(tokens[tokens.length - 1])) tokens.pop();

      if (tokens.length) return tokens.join('');
    }

    const pattern = this.phxDateFormat || DEFAULT_DATE_FORMAT;
    if (this.phxHideYear) {
      return (HIDE_YEAR_FORMAT_MAPPER[pattern] || DEFAULT_DATE_FORMAT_WITHOUT_YEAR).toUpperCase();
    }
    return pattern.toUpperCase();
  }

  private formatDateForMessage(date: Date): string {
    if (this._phxLocale) {
      return this._phxLocale.formatDate(date, PhxDateFormatStyle.DEFAULT, { skipTimezone: true });
    }
    return format(date, this.phxDateFormat || DEFAULT_DATE_FORMAT, { locale: this._datePickerService.getDateFnsLocale() });
  }

  private clearInputBuffer(): void {
    this._inputBuffer = '';
    if (this._inputBufferTimeout) {
      clearTimeout(this._inputBufferTimeout);
      this._inputBufferTimeout = null;
    }
  }

  private showTooltipMessage(title: string | null, content: string, autoHideMs?: number): void {
    const isSameTooltip = this.tooltipTitle === title && this.tooltipContent === content;
    const wasVisible = this._inputTooltip ? (this._inputTooltip as any).tooltip?.visible : false;
    if (wasVisible) {
      this.clearTooltipAutoHideTimeout();
      if (!isSameTooltip) {
        this.tooltipTitle = title;
        this.tooltipContent = content;
        this._cdr.detectChanges();
      }
      if (autoHideMs) {
        this._tooltipAutoHideTimeout = setTimeout(() => this.dismissTooltip(), autoHideMs);
      }
      return;
    }

    this.tooltipTitle = title;
    this.tooltipContent = content;
    this._cdr.detectChanges();
    this.clearTooltipAutoHideTimeout();
    this._inputTooltip?.showTooltip();

    if (autoHideMs) {
      this._tooltipAutoHideTimeout = setTimeout(() => this.dismissTooltip(), autoHideMs);
    }
  }

  private dismissTooltip(): void {
    this.clearTooltipAutoHideTimeout();
    this._inputTooltip?.hideTooltip();
    this.tooltipTitle = null;
    this.tooltipContent = null;
  }

  private clearTooltipAutoHideTimeout(): void {
    if (this._tooltipAutoHideTimeout) {
      clearTimeout(this._tooltipAutoHideTimeout);
      this._tooltipAutoHideTimeout = null;
    }
  }

  private stripDiacritics(str: string): string {
    return str.normalize('NFD').replace(DIACRITICS_REGEX, '');
  }

  /** Fixed reference date used for formatToParts — chosen so day/month/year are unambiguous. */
  private static readonly REFERENCE_DATE = new Date(2026, 0, 15);

  private static readonly INTL_TYPE_TO_FIELD: Record<string, PhxNonNullDateInputField> = {
    month: DATE_INPUT_FIELD.MONTH,
    day: DATE_INPUT_FIELD.DAY,
    year: DATE_INPUT_FIELD.YEAR,
  };

  private parseIntlParts(parts: Intl.DateTimeFormatPart[]): {
    fieldOrder: PhxNonNullDateInputField[];
    separators: string[];
    monthDisplay: 'numeric' | 'alpha';
  } | null {
    const fieldOrder: PhxNonNullDateInputField[] = [];
    const separators: string[] = [];
    let monthDisplay: 'numeric' | 'alpha' = 'numeric';

    for (const part of parts) {
      const field = PhxDatePickerInputComponent.INTL_TYPE_TO_FIELD[part.type];
      if (field) {
        fieldOrder.push(field);
        if (field === DATE_INPUT_FIELD.MONTH && !/^\d+$/.test(part.value)) {
          monthDisplay = 'alpha';
        }
      } else if (part.type === 'literal' && fieldOrder.length > 0 && fieldOrder.length <= 2) {
        separators.push(part.value);
      }
    }

    if (fieldOrder.length < 2 || fieldOrder.length > 3) return null;
    return { fieldOrder, separators, monthDisplay };
  }
}
